import React from "react";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoIcon from "@mui/icons-material/Info";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";
import CircularProgress from "@mui/material/CircularProgress";

function DataReviewStep({ logic, categoriasStock }) {
  // Gerar árvore de categorias para o select
  const categoriesTree = logic.buildCategoryTree(categoriasStock);
  const categoryOptions = logic.renderCategoryOptions(categoriesTree);

  // Garantir que a primeira categoria seja selecionada por padrão, se necessário
  React.useEffect(() => {
    if (categoriasStock && categoriasStock.length > 0) {
      const firstCategoryId = categoriasStock[0].id;
      logic.handleCategoriaPrincipalChange(firstCategoryId);
    }
  }, [categoriasStock]);

  React.useEffect(() => {
    if (
      categoriasStock &&
      Array.isArray(categoriasStock) &&
      categoriasStock.length > 0
    ) {
      // Usar a primeira categoria como padrão
      const firstCategoryId = categoriasStock[0].id;

      // Forçar atualização de todos os itens
      logic.handleCategoriaPrincipalChange(firstCategoryId);
    }
  }, [categoriasStock]);
  return (
    <div className="enhanced-import-step data-review-step">
      <div className="data-review-header">
        <h3>Revisão e Edição de Dados</h3>
        <p>
          Revise os dados importados e faça ajustes se necessário.
          <Tooltip title="Edite qualquer campo clicando diretamente nele.">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </p>
      </div>

      {/* Opções padrão para todos os produtos */}
      <div className="data-review-defaults">
        <div className="data-review-default-item">
          <div className="data-review-default-label">
            <label>Categoria padrão:</label>
            <Tooltip title="Esta categoria será aplicada a todos os produtos novos. Você pode alterá-la individualmente abaixo.">
              <IconButton size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {logic.isProcessingData && (
              <CircularProgress size={16} className="data-review-spinner" />
            )}
          </div>

          <select
            value={logic.categoriaPrincipal || ""}
            onChange={(e) =>
              logic.handleCategoriaPrincipalChange(e.target.value)
            }
            className="data-review-default-select"
            disabled={logic.isProcessingData}
          >
            {categoryOptions && categoryOptions.length > 0 ? (
              categoryOptions.map((option) => (
                <option
                  key={option.id}
                  value={option.value}
                  style={{
                    paddingLeft: `${option.level * 10}px`,
                    fontWeight: option.level === 0 ? "bold" : "normal",
                  }}
                >
                  {option.level > 0
                    ? `${"\u00A0".repeat(option.level * 2)}└─ ${option.label}`
                    : option.label}
                </option>
              ))
            ) : (
              <option value="" disabled>
                Nenhuma categoria disponível
              </option>
            )}
          </select>
        </div>

        {/* Fornecedor padrão usando select normal para combinar com categoria */}
        <div className="data-review-default-item">
          <div className="data-review-default-label">
            <label>Fornecedor padrão:</label>
            <Tooltip title="Selecione um fornecedor para aplicar a todos os produtos. Pode ser alterado individualmente depois.">
              <IconButton size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {logic.isProcessingData && (
              <CircularProgress size={16} className="data-review-spinner" />
            )}
          </div>

          <select
            defaultValue=""
            onChange={(e) => logic.handleFornecedorPadraoChange(e.target.value)}
            className="data-review-default-select"
            disabled={logic.isProcessingData}
          >
            <option value="">Selecione um fornecedor</option>
            {logic.fornecedoresList.map((fornecedor) => (
              <option key={fornecedor.id} value={fornecedor.id}>
                {fornecedor.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Armazém padrão */}
        <div className="data-review-default-item">
          <div className="data-review-default-label">
            <label>Armazém padrão:</label>
            <Tooltip title="Aplica este armazém a todos os produtos.">
              <IconButton size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          <select
            value={logic.armazemPadrao || ""}
            onChange={(e) => logic.handleArmazemPadraoChange(e.target.value)}
            className="data-review-default-select"
            disabled={logic.isProcessingData}
          >
            <option value="">Selecione um armazém</option>
            {(logic.armazensListImport || []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.codigo} — {a.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Localização padrão */}
        <div className="data-review-default-item">
          <div className="data-review-default-label">
            <label>Localização padrão:</label>
            <Tooltip title="Aplica esta localização a todos os produtos.">
              <IconButton size="small">
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
          <select
            value={logic.localizacaoPadrao || ""}
            onChange={(e) =>
              logic.handleLocalizacaoPadraoChange(e.target.value)
            }
            className="data-review-default-select"
            disabled={logic.isProcessingData || !logic.armazemPadrao}
          >
            <option value="">Selecione uma localização</option>
            {(logic.localizacoesList || [])
              .filter(
                (l) =>
                  l.armazem?.id === logic.armazemPadrao ||
                  l.armazem === logic.armazemPadrao,
              )
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} — {l.nome}
                </option>
              ))}
          </select>
        </div>

        {/* Opção para usar lotes automáticos - Movida para aqui */}
        <div className="data-review-default-item data-review-auto-lotes">
          <FormControlLabel
            control={
              <Checkbox
                checked={logic.usarLotesAutomaticos}
                onChange={(e) =>
                  logic.setUsarLotesAutomaticos(e.target.checked)
                }
                color="primary"
              />
            }
            label="Gerar lotes automáticos para itens sem lote"
            className="data-review-lotes-checkbox"
          />
          <Tooltip title="Quando ativado, um código de lote será gerado automaticamente para produtos sem lote específico.">
            <IconButton size="small">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Indicador de carregamento global para a tabela */}
      {logic.isProcessingData && (
        <div className="data-review-loading-overlay">
          <CircularProgress size={40} className="data-review-loading-spinner" />
          <p className="data-review-loading-message">
            Processando... Aguarde enquanto os dados são atualizados.
          </p>
        </div>
      )}

      {/* Tabela de revisão de dados */}
      <div className="data-review-table-container">
        <table className="data-review-table">
          <thead>
            <tr>
              <th>Nome do Produto</th>
              <th>Qtd</th>
              <th>Stock min</th>
              <th>Lote</th>
              <th>Validade</th>
              <th>Un. Medida</th>
              <th>Categoria</th>
              <th>Classificação</th>
              <th>Preço Unit.</th>
              <th>Fornecedor</th>
              <th colSpan={2}>Armazém / Localização</th>
            </tr>
          </thead>
          <tbody
            className={
              logic.isProcessingData ? "data-review-table-loading" : ""
            }
          >
            {logic.processedData.map((item) => (
              <tr key={item.id} className="data-review-row">
                <td>
                  <TextField
                    value={item.nome}
                    onChange={(e) =>
                      logic.handleItemNameChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    error={!item.nome}
                    helperText={!item.nome ? "Obrigatório" : ""}
                  />
                </td>
                <td>
                  <TextField
                    type="number"
                    value={item.quantidade}
                    onChange={(e) =>
                      logic.handleItemQuantityChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    error={item.quantidade < 0}
                    helperText={item.quantidade < 0 ? "Negativo" : ""}
                  />
                </td>
                <td>
                  <TextField
                    type="number"
                    value={item.quantidadeIdeal ?? ""}
                    onChange={(e) =>
                      logic.handleItemIdealQuantityChange(
                        item.id,
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                    placeholder="Auto"
                  />
                </td>
                <td>
                  <TextField
                    value={item.lote}
                    onChange={(e) =>
                      logic.handleItemBatchChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    placeholder={
                      logic.usarLotesAutomaticos
                        ? "Gerado Automaticamente"
                        : "N/A"
                    }
                    disabled={logic.usarLotesAutomaticos}
                    InputProps={{
                      readOnly: logic.usarLotesAutomaticos,
                    }}
                  />
                </td>
                <td>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DesktopDatePicker
                      inputFormat="DD/MM/YYYY"
                      value={
                        item.dataValidade ? moment(item.dataValidade) : null
                      }
                      onChange={(date) =>
                        logic.handleItemExpiryDateChange(item.id, date)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </LocalizationProvider>
                </td>
                <td>
                  <TextField
                    select
                    value={item.unidadeMedida}
                    onChange={(e) =>
                      logic.handleItemUnitChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    {logic.UNIDADES_MEDIDA.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </td>
                <td>
                  <TextField
                    select
                    value={item.categoriaStock || ""}
                    onChange={(e) =>
                      logic.handleItemCategoryChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    {categoryOptions && categoryOptions.length > 0 ? (
                      categoryOptions.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.value}
                          style={{
                            paddingLeft: `${option.level * 10}px`,
                          }}
                        >
                          {option.level > 0
                            ? `${"\u00A0".repeat(option.level * 2)}└─ ${
                                option.label
                              }`
                            : option.label}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        Nenhuma categoria disponível
                      </MenuItem>
                    )}
                  </TextField>
                </td>
                <td>
                  <TextField
                    select
                    value={item.classificacao}
                    onChange={(e) =>
                      logic.handleItemClassificacaoChange(
                        item.id,
                        e.target.value,
                      )
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="PA">Produto Acabado</MenuItem>
                    <MenuItem value="MP">Matéria Prima</MenuItem>
                  </TextField>
                </td>
                <td>
                  <TextField
                    type="number"
                    value={item.precoCompra || ""}
                    onChange={(e) =>
                      logic.handleItemPriceChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                </td>
                <td>
                  <TextField
                    select
                    value={item.fornecedor || ""}
                    onChange={(e) =>
                      logic.handleItemSupplierChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {logic.fornecedoresList.map((fornecedor) => (
                      <MenuItem key={fornecedor.id} value={fornecedor.id}>
                        {fornecedor.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                </td>
                <td colSpan={2} style={{ minWidth: 200 }}>
                  {/* Armazém */}
                  <TextField
                    select
                    value={item.armazem || ""}
                    onChange={(e) =>
                      logic.handleItemArmazemChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    label="Armazém"
                    error={!item.armazem === true}
                  >
                    {(logic.armazensListImport || []).map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.codigo} — {a.nome}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Localização */}
                  <TextField
                    select
                    value={item.localizacao || ""}
                    onChange={(e) =>
                      logic.handleItemLocalizacaoChange(item.id, e.target.value)
                    }
                    variant="standard"
                    fullWidth
                    size="small"
                    label="Localização"
                    disabled={!item.armazem}
                    error={!!(item.armazem && !item.localizacao)}
                  >
                    {(logic.localizacoesList || [])
                      .filter(
                        (l) =>
                          l.armazem?.id === item.armazem ||
                          l.armazem === item.armazem,
                      )
                      .map((l) => (
                        <MenuItem key={l.id} value={l.id}>
                          {l.codigo} — {l.nome}
                        </MenuItem>
                      ))}
                  </TextField>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataReviewStep;
